/**
 * Edge Routing Utilities
 * Specialized functions for routing and bundling edges in workflow diagrams
 */
import { getNodeCategory } from '../utils/node-type-utils';

class EdgeRouter {
  /**
   * Initialize edge router with graph data
   */
  constructor() {
    this.edges = [];
    this.nodes = [];
  }

  /**
   * Prepare edges for routing by adding default properties
   * @param {Array} edges - The edges to prepare
   * @param {Map} nodeMap - Map of node ID to node objects
   * @returns {Array} Prepared edges
   */
  static prepareEdges(edges, nodeMap) {
    // Track parent-child relationships for better edge routing
    const parentChildMap = new Map();

    // Build parent-child map from nodes
    Array.from(nodeMap.values()).forEach((node) => {
      if (node.parentNode) {
        parentChildMap.set(node.id, node.parentNode);
      }
    });

    return edges.map((edge) => {
      const sourceNode = nodeMap.get(edge.source);
      const targetNode = nodeMap.get(edge.target);

      if (!sourceNode || !targetNode) {
        return edge;
      }

      // Get parent frames (if any) for source and target
      const sourceParent = parentChildMap.get(edge.source);
      const targetParent = parentChildMap.get(edge.target);

      // Detect if this is an edge between nodes in different frames or between
      // a node in a frame and one outside
      const isInterFrameEdge = sourceParent !== targetParent && (sourceParent || targetParent);

      // Apply special routing for inter-frame edges
      if (isInterFrameEdge) {
        return this.routeInterFrameEdge(edge, sourceNode, targetNode);
      }

      // Regular routing for same-frame or no-frame edges
      return this.routeSmoothStep(edge);
    });
  }

  /**
   * Route an edge using step line with smooth corners
   * @param {Object} edge - The edge to route
   * @param {Object} sourceNode - Source node
   * @param {Object} targetNode - Target node
   * @returns {Object} Edge with routing information
   */
  static routeSmoothStep(edge) {
    // Use built-in smoothstep routing with consistent default type
    return {
      ...edge,
      type: 'default',
      // sourceHandle: 'right',
      // targetHandle: 'left',
      animated: true,
      style: edge.style || {
        strokeWidth: 2,
      },
    };
  }

  /**
   * Route an edge between nodes in different frames
   * @param {Object} edge - The edge to route
   * @param {Object} sourceNode - Source node
   * @param {Object} targetNode - Target node
   * @returns {Object} Edge with routing information
   */
  static routeInterFrameEdge(edge, sourceNode, targetNode) {
    return {
      ...edge,
      type: 'default', // Use consistent edge type
      // sourceHandle: 'right',
      // targetHandle: 'left',
      animated: true,
      style: {
        ...(edge.style || {}),
        strokeWidth: 2,
        strokeDasharray: '5 5', // Make inter-frame edges dashed
        stroke: '#999',
      },
    };
  }
}

export default EdgeRouter;
